/**
 * QuestionContentV2 - Renders question context and stem
 *
 * Features:
 * - Markdown and LaTeX/MathML rendering
 * - HTML content sanitization
 * - Responsive typography
 * - Image handling
 * - Desmos graphing calculator integration
 */

import { memo, lazy, Suspense } from 'react'
import { renderContent } from '../../../utils/contentRenderer'
import { useDesmosAvailable } from '../../../hooks/useDesmos'
import { useImageLightbox } from '../../../hooks/useImageLightbox'
import ImageLightbox from '../../ImageLightbox'

// Lazy load DesmosGraph to avoid loading Desmos API until needed
const DesmosGraph = lazy(() => import('../../DesmosGraph'))

const QuestionContentV2 = memo(({ question }) => {
  const { lightboxState, closeLightbox, containerRef } = useImageLightbox()

  if (!question) return null

  const contentType = question.contentType || question.content_type

  // Check if context exists and has content
  const hasContext =
    question.context && typeof question.context === 'string' && question.context.trim().length > 0

  return (
    <div ref={containerRef} className="space-y-4">
      {/* Context/Passage (if exists) */}
      {hasContext && (
        <div className="question-content prose prose-sm sm:prose dark:prose-invert max-w-none">
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 sm:p-5 border border-gray-200 dark:border-gray-700">
            {renderContent(question.context, contentType)}
          </div>
        </div>
      )}

      {/* Question Stem */}
      {question.questionText && (
        <div
          className={`question-content prose prose-sm sm:prose dark:prose-invert max-w-none ${
            hasContext ? 'font-medium' : ''
          }`}
        >
          {renderContent(question.questionText, contentType)}
        </div>
      )}

      {/* Question Image (if any) */}
      {question.imageUrl && (
        <div className="mt-4">
          <img
            src={question.imageUrl}
            alt="Question illustration"
            className="max-w-full h-auto rounded-lg shadow-sm mx-auto cursor-zoom-in"
            loading="lazy"
          />
        </div>
      )}

      {/* Desmos Graph (if question has Desmos configuration) */}
      {question.mathDetails?.desmos_state && (
        <div className="mt-4">
          <Suspense
            fallback={
              <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 h-[400px]">
                <div className="text-center text-gray-500 dark:text-gray-400">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-2" />
                  <p className="text-sm">Loading graph...</p>
                </div>
              </div>
            }
          >
            <DesmosGraph
              graphState={question.mathDetails.desmos_state}
              interactive={true}
              showExpressions={question.mathDetails.desmos_settings?.showExpressions ?? true}
              lockViewport={question.mathDetails.desmos_settings?.lockViewport ?? false}
              showKeypad={question.mathDetails.desmos_settings?.showKeypad ?? false}
              height={question.mathDetails.desmos_settings?.height ?? 400}
              allowFullscreen={true}
            />
          </Suspense>
        </div>
      )}
      <ImageLightbox
        src={lightboxState.src}
        alt={lightboxState.alt}
        isOpen={lightboxState.isOpen}
        onClose={closeLightbox}
      />
    </div>
  )
})

QuestionContentV2.displayName = 'QuestionContentV2'

export default QuestionContentV2
